import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Manuscript } from "../api/types";
import { Icon } from "../components/icons";
import { ManuscriptPane } from "../components/ManuscriptPane";
import { ScriptMarksSummary } from "../components/ScriptMarksSummary";

interface ReviewResponse {
  project: { id: string; name: string };
  manuscript: Manuscript;
  n_pages: number;
  n_tokens: number;
  pdf_url: string;
  ocr?: { applied?: boolean; recovered_pages?: number[] } | null;
  glyph_repairs?: {
    words: number;
    examples?: [string, string][];
    unresolved?: number;
  } | null;
}

// A pre-audio look at the parsed manuscript, rendered exactly as it'll appear in
// the chapter reports (same ManuscriptPane, same studio paper) — so the user can
// catch a bad extraction (garbled text, missing headings, front matter swept in)
// before spending time and ElevenLabs minutes on audio.
export function ManuscriptReview() {
  const { projectId } = useParams();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["manuscript-review", projectId],
    queryFn: () =>
      api<ReviewResponse>(`/api/modules/proofing/projects/${projectId}/manuscript`),
    staleTime: 5 * 60 * 1000, // the parse is cached server-side; don't refetch on focus
    retry: false,
  });

  return (
    <div className="report studio review-only">
      <div className="report-head">
        <Link to={`/projects/${projectId}`} className="btn ghost sm">
          <Icon name="back" size={15} /> Back to project
        </Link>
        <div className="rh-title">
          <strong>{data?.project?.name ?? "Manuscript"}</strong>
          <span className="muted"> · Review Manuscript</span>
        </div>
        <div className="row" style={{ gap: 12 }}>
          {data && (
            <span className="muted" style={{ fontSize: 12.5, whiteSpace: "nowrap" }}>
              {data.n_pages} pages · {data.n_tokens.toLocaleString()} words
            </span>
          )}
          {data?.pdf_url && (
            <a className="btn ghost sm" href={data.pdf_url} target="_blank" rel="noopener">
              <Icon name="document" size={15} /> Original PDF
            </a>
          )}
        </div>
      </div>

      <div className="report-body">
        <div className="pane-left scroll">
          {isLoading ? (
            <div className="ms-state">
              <span className="spinner" />
              <div>
                <strong>Parsing the manuscript…</strong>
                <p className="muted">
                  Reading the PDF and laying it out the way it'll appear in your reports.
                  This can take a moment the first time — it's cached afterwards.
                </p>
              </div>
            </div>
          ) : isError ? (
            <div className="ms-state">
              <Icon name="info" size={22} />
              <div>
                <strong>We couldn't show this manuscript.</strong>
                <p className="muted">
                  {(error as any)?.message || "Try re-uploading the PDF from the project page."}
                </p>
              </div>
            </div>
          ) : data ? (
            <>
              <div className="ms-review-note">
                This is the text the proofer will compare against the narration. Grey,
                italic lines are <b>excluded</b> from proofing (front matter, stage
                directions). If whole passages look like garbage here, the PDF's text
                layer is corrupt — re-export it or request a clean copy.
              </div>
              {data.ocr?.applied && (
                <div className="ms-ocr-note">
                  <Icon name="info" size={15} /> Some pages had a corrupted text layer and
                  were recovered automatically with OCR
                  {data.ocr.recovered_pages?.length
                    ? ` (p. ${data.ocr.recovered_pages.join(", ")})`
                    : ""}
                  . Double-check those pages read correctly.
                </div>
              )}
              {data.glyph_repairs?.words ? (
                <GlyphRepairNote repairs={data.glyph_repairs} />
              ) : null}
              <ScriptMarksSummary projectId={projectId!} />
              <ManuscriptPane
                manuscript={data.manuscript}
                discrepancies={[]}
                activeId={null}
                selectedId={null}
                onSelect={() => {}}
                pdfUrl={data.pdf_url}
                projectId={projectId}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// The PDF's font labelled its fi / fl / ff letter pairs as punctuation, so its
// text read "!rst" for "first"; the extractor put the letters back.
function GlyphRepairNote({ repairs }: { repairs: NonNullable<ReviewResponse["glyph_repairs"]> }) {
  const ex = repairs.examples?.[0];
  const left = repairs.unresolved ?? 0;
  return (
    <div className="ms-ocr-note ms-fix-note">
      <Icon name="info" size={15} />
      <span>
        This PDF's font stores letter pairs like <i>fi</i>, <i>fl</i> and <i>ff</i> as
        punctuation, so its text reads{" "}
        {ex ? <>“{ex[0]}” for “{ex[1]}”</> : "garbled words"}. We repaired{" "}
        {repairs.words.toLocaleString()} {repairs.words === 1 ? "word" : "words"} automatically.
        {left > 0 &&
          ` ${left} ${left === 1 ? "word uses a pair we couldn't identify and still reads" : "words use pairs we couldn't identify and still read"} garbled.`}
      </span>
    </div>
  );
}
